import type { LucideIcon } from 'lucide-react';
import { BookOpen, Globe, Mic, RefreshCw, ShieldCheck, Sparkles, Zap } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { AgentTemplate } from '../../../types/agent';

/** English first, then major Indian languages — same meaning: speak with AI in your language. */
const MULTILINGUAL_TAGLINE: { label: string; line: string }[] = [
  { label: 'English', line: 'Speak with AI in your language' },
  { label: 'हिंदी · Hindi', line: 'अपनी भाषा में AI से बात करें' },
  { label: 'தமிழ் · Tamil', line: 'உங்கள் மொழியில் AI உடன் பேசுங்கள்' },
  { label: 'తెలుగు · Telugu', line: 'మీ భాషలో AI తో మాట్లాడండి' },
  { label: 'বাংলা · Bengali', line: 'আপনার ভাষায় AI এর সাথে কথা বলুন' },
  { label: 'मराठी · Marathi', line: 'तुमच्या भाषेत AI शी बोला' },
  { label: 'ગુજરાતી · Gujarati', line: 'તમારી ભાષામાં AI સાથે વાત કરો' },
  { label: 'ಕನ್ನಡ · Kannada', line: 'ನಿಮ್ಮ ಭಾಷೆಯಲ್ಲಿ AI ಯೊಂದಿಗೆ ಮಾತನಾಡಿ' },
  { label: 'മലയാളം · Malayalam', line: 'നിങ്ങളുടെ ഭാഷയിൽ AI യുമായി സംസാരിക്കുക' },
  { label: 'ਪੰਜਾਬੀ · Punjabi', line: 'ਆਪਣੀ ਭਾਸ਼ਾ ਵਿੱਚ AI ਨਾਲ ਗੱਲ ਕਰੋ' },
  { label: 'اردو · Urdu', line: 'اپنی زبان میں AI سے بات کریں' },
];

const TAGLINE_ROTATE_MS = 3200;

export type MultilingualTaglineVariant = 'onboarding' | 'banner';

/** Rotating multilingual tagline — onboarding: below primary actions in card; banner: hero / empty chat. */
export function MultilingualTagline({ variant = 'onboarding' }: { variant?: MultilingualTaglineVariant }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % MULTILINGUAL_TAGLINE.length);
    }, TAGLINE_ROTATE_MS);
    return () => window.clearInterval(id);
  }, []);

  const current = MULTILINGUAL_TAGLINE[index]!;

  const inner = (
    <>
      <p className="sr-only">
        Speak with AI in your language. This experience highlights support for English, Hindi, Tamil, Telugu,
        Bengali, Marathi, Gujarati, Kannada, Malayalam, Punjabi, and Urdu.
      </p>
      <div
        className={
          variant === 'banner'
            ? 'mx-auto flex min-h-[5rem] w-full max-w-xl flex-col items-center justify-center gap-1.5 rounded-2xl border border-subtle/60 bg-gradient-to-b from-brand/[0.12] via-brand/[0.05] to-transparent px-4 py-3 text-center shadow-lg shadow-black/15 ring-1 ring-white/[0.06] sm:min-h-[5.25rem] sm:px-5'
            : 'flex min-h-[4.75rem] flex-col items-center justify-center gap-1 rounded-2xl border border-subtle/60 bg-gradient-to-b from-brand/[0.08] to-transparent px-3 py-2.5 text-center shadow-inner shadow-black/10'
        }
        aria-hidden="true"
      >
        <span
          className={
            variant === 'banner'
              ? 'text-[11px] font-semibold uppercase tracking-[0.2em] text-accent'
              : 'text-[10px] font-semibold uppercase tracking-[0.18em] text-accent/90'
          }
        >
          {current.label}
        </span>
        <p
          key={index}
          className={
            variant === 'banner'
              ? 'animate-lang-tagline-in max-w-[22rem] text-base font-semibold leading-snug text-th-primary sm:text-lg'
              : 'animate-lang-tagline-in max-w-[20rem] text-[15px] font-semibold leading-snug text-th-primary sm:text-base'
          }
          lang={index === 0 ? 'en' : undefined}
        >
          {current.line}
        </p>
      </div>
    </>
  );

  if (variant === 'banner') {
    return (
      <div className="w-full px-0">
        {inner}
      </div>
    );
  }

  return (
    <div className="mt-4 border-t border-subtle/60 pt-4">
      {inner}
    </div>
  );
}

/** Trust / feature cards — place at bottom of onboarding column (e.g. after main card). */
export function PublicAgentOnboardingTrustCards({
  template,
  embedInPanel = false,
}: {
  template: AgentTemplate;
  /** When the chat is in the end drawer, use a vertical stack on md+ instead of a horizontal scroller. */
  embedInPanel?: boolean;
}) {
  const cards = useMemo(() => {
    const list: { icon: LucideIcon; title: string; description: string }[] = [
      {
        icon: ShieldCheck,
        title: 'Built for privacy',
        description: 'Your session is isolated; chats stay between you and this assistant.',
      },
      {
        icon: Sparkles,
        title: 'Purpose-built AI',
        description: "Guided by this agent's role, instructions, and safety rules.",
      },
      {
        icon: Zap,
        title: 'Fast, clear answers',
        description: 'Straightforward replies when you need a decision or next step.',
      },
    ];

    if (template.knowledge_base_enabled) {
      list.push({
        icon: BookOpen,
        title: 'Grounded context',
        description: 'Draws on curated knowledge so answers stay relevant to this agent.',
      });
    }

    if (template.tool_tavily || template.tool_serper) {
      list.push({
        icon: Globe,
        title: 'Live information',
        description: 'Can fetch timely facts when web search is enabled for this agent.',
      });
    }

    if (template.voice_enabled) {
      list.push({
        icon: Mic,
        title: 'Natural voice',
        description: 'Talk instead of type when voice is on—hands-free and conversational.',
      });
    }

    list.push({
      icon: RefreshCw,
      title: 'Steady improvements',
      description: 'Models and guardrails evolve so quality and safety keep pace.',
    });

    return list;
  }, [template]);

  const scrollerClass = embedInPanel
    ? 'flex touch-pan-x gap-4 overflow-x-auto overscroll-x-contain scroll-pl-4 scroll-pr-4 px-4 pb-1 pt-0.5 scrollbar-hide sm:scroll-pl-6 sm:scroll-pr-6 sm:px-6 sm:pb-2 snap-x snap-mandatory md:flex-col md:touch-pan-y md:gap-3 md:overflow-x-hidden md:overflow-y-visible md:overscroll-y-contain md:scroll-pl-0 md:scroll-pr-0 md:px-0 md:pt-0 md:pb-2 md:snap-none'
      : 'flex touch-pan-x gap-4 overflow-x-auto overscroll-x-contain scroll-pl-4 scroll-pr-4 px-4 pb-1 pt-0.5 scrollbar-hide sm:scroll-pl-6 sm:scroll-pr-6 sm:px-6 sm:pb-2 snap-x snap-mandatory';

  return (
    <section aria-label="Trust highlights">
      <div className={embedInPanel ? '-mx-4 sm:-mx-6 md:mx-0' : '-mx-4 sm:-mx-6'}>
        <div
          className={scrollerClass}
          role="list"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {cards.map((card, i) => {
            const Icon = card.icon;
            return (
              <div
                key={`${card.title}-${i}`}
                role="listitem"
                className={
                  embedInPanel
                    ? 'flex w-[min(22rem,calc(100vw-2.25rem))] shrink-0 snap-center snap-always flex-col gap-3 rounded-2xl border border-subtle/60 bg-gradient-to-b from-surface/80 to-elevated/40 p-5 shadow-md shadow-black/20 ring-1 ring-white/[0.04] md:w-full md:min-w-0'
                    : 'flex w-[min(22rem,calc(100vw-2.25rem))] shrink-0 snap-center snap-always flex-col gap-3 rounded-2xl border border-subtle/60 bg-gradient-to-b from-surface/80 to-elevated/40 p-5 shadow-md shadow-black/20 ring-1 ring-white/[0.04]'
                }
              >
                <div className="flex shrink-0 items-start gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand/20 text-brand shadow-inner shadow-black/20">
                    <Icon className="h-5 w-5" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <h3 className="text-[15px] font-semibold leading-snug text-th-primary">{card.title}</h3>
                    <p className="text-[13px] leading-relaxed text-th-secondary">{card.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
